/**
 * SpeedDial — 화면 오른쪽 아래 둥근 버튼(FAB). 누르면 동작 목록이 아래에서 차례로 펼쳐진다.
 * ----------------------------------------------------------------------------
 * 폰(모바일 셸) 전용. 데모·근거: docs/MOBILE_WEBAPP.md §7 (Material 3 FAB menu).
 *  - 항목은 4개 이하 권장. 배경(딤)을 누르거나 ESC, 항목을 누르면 닫힌다.
 *  - selectedKey 를 주면 고른 항목에 체크 표시(채널 전환처럼 "고르기"로 쓸 때).
 *  - 위치는 하단 탭 위(safe-area 포함). 한 화면에 하나만(파란 면 하나 — 폰 레스토프).
 *  - 모션 0.18–0.25초, 동작 줄이기면 끔.
 */
import React from "react";
import { Check, Plus } from "lucide-react";
import { useLanguage } from "./LanguageProvider";

export type SpeedDialItem = { key: string; label: string; icon?: React.ReactNode; onSelect: () => void };

export default function SpeedDial({
  items,
  label,
  icon,
  selectedKey,
  bottomOffset = 72,
}: {
  items: SpeedDialItem[];
  /** 둥근 버튼의 이름(화면 읽기용) */
  label: string;
  /** 닫혀 있을 때 아이콘(기본 +). 열리면 45도 돌아 × 가 된다. */
  icon?: React.ReactNode;
  selectedKey?: string;
  /** 화면 아래에서 띄울 거리(px) — 하단 탭(56px) + 여백 */
  bottomOffset?: number;
}) {
  const { t } = useLanguage();
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);
  const n = items.length;
  return (
    <div className="ds-scope" data-speed-dial="">
      <div
        aria-hidden
        onClick={() => setOpen(false)}
        className={`fixed inset-0 transition-opacity duration-base ease-out motion-reduce:transition-none ${open ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"}`}
        style={{ zIndex: 1040, background: "var(--overlay)" }}
      />
      <div
        className="fixed right-4 flex flex-col items-end gap-3"
        style={{ zIndex: 1041, bottom: `calc(${bottomOffset}px + env(safe-area-inset-bottom))` }}
      >
        <div role="menu" aria-label={label} className="flex flex-col items-end gap-2.5">
          {items.map((it, i) => {
            const on = it.key === selectedKey;
            return (
              <button
                key={it.key}
                type="button"
                role="menuitem"
                tabIndex={open ? 0 : -1}
                onClick={() => { setOpen(false); it.onSelect(); }}
                className={`flex items-center gap-2.5 border-0 bg-transparent p-0 transition-all ease-pop motion-reduce:transition-none ${open ? "pointer-events-auto translate-y-0 scale-100 opacity-100" : "pointer-events-none translate-y-3 scale-90 opacity-0"}`}
                style={{ transitionDuration: "220ms", transitionDelay: open ? `${(n - 1 - i) * 30}ms` : "0ms" }}
              >
                <span className={`rounded-control bg-canvas px-2.5 py-1.5 text-[13px] font-semibold shadow-card ${on ? "text-primary-strong" : "text-ink"}`} style={{ boxShadow: "0 2px 8px rgba(31,42,68,.14)" }}>
                  {it.label}
                </span>
                <span className={`grid h-11 w-11 place-items-center rounded-pill bg-canvas ${on ? "text-primary" : "text-primary-strong"}`} style={{ boxShadow: "0 3px 10px rgba(31,42,68,.18)" }}>
                  {on ? <Check size={18} aria-hidden /> : it.icon}
                </span>
              </button>
            );
          })}
        </div>
        <button
          type="button"
          aria-label={open ? t("닫기", "Close") : label}
          aria-expanded={open}
          aria-haspopup="menu"
          onClick={() => setOpen((v) => !v)}
          className="grid h-14 w-14 place-items-center rounded-pill border-0 bg-primary text-ink-inverse active:scale-95"
          style={{ boxShadow: "0 6px 16px rgba(0,91,246,.35)" }}
        >
          <span className={`grid place-items-center transition-transform duration-base ease-pop motion-reduce:transition-none ${open ? "rotate-45" : ""}`}>
            {open || !icon ? <Plus size={24} aria-hidden /> : icon}
          </span>
        </button>
      </div>
    </div>
  );
}
